/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView } from '@components/atoms'

import { useAppSelector, useAppDispatch, logDashboardActions } from '@redux/index'
import type { LogRecipientType } from '@redux/types'

import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'

const LogSettingsPage = () => {
	const dispatch = useAppDispatch()

	const logSetting = useAppSelector(state => state.logDashboard.logSetting)
	const logSettingStatus = useAppSelector(state => state.logDashboard.logSettingStatus)

	const [newEmail, setNewEmail] = React.useState('')
	const [newName, setNewName] = React.useState('')

	React.useEffect(() => {
		dispatch(logDashboardActions.TRIGGER_GET_LOG_SETTING(true))
	}, [])

	const handleToggleActive = (idx: number) => {
		if (!logSetting) return
		const newRecipients = logSetting.recipients.map((r: LogRecipientType, i: number) =>
			i === idx ? { ...r, active: !r.active } : r,
		)
		dispatch(
			logDashboardActions.LOG_SETTING({ ...logSetting, recipients: newRecipients }),
		)
	}

	const handleDelete = (idx: number) => {
		if (!logSetting) return
		const newRecipients = logSetting.recipients.filter(
			(_: LogRecipientType, i: number) => i !== idx,
		)
		dispatch(
			logDashboardActions.LOG_SETTING({ ...logSetting, recipients: newRecipients }),
		)
	}

	const handleAdd = () => {
		if (!newEmail || !newName || !logSetting) return
		const newRecipient: LogRecipientType = {
			email: newEmail,
			name: newName,
			active: true,
		}
		dispatch(
			logDashboardActions.LOG_SETTING({
				...logSetting,
				recipients: [...logSetting.recipients, newRecipient],
			}),
		)
		setNewEmail('')
		setNewName('')
	}

	const handleSave = () => {
		dispatch(logDashboardActions.TRIGGER_UPDATE_LOG_SETTING(true))
	}

	const isLoading = logSettingStatus === 'loading'

	return (
		<View css={style}>
			<BoxView
				title="이메일 수신자 설정"
				rightHeaderAccessory={
					<Button
						variant="contained"
						startIcon={<SaveIcon />}
						onClick={handleSave}
						disabled={isLoading}
					>
						저장
					</Button>
				}
			>
				<View className="settingsContainer">
					{isLoading ? (
						<View className="loadingView">
							<CircularProgress />
						</View>
					) : (
						<>
							{/* Info */}
							{logSetting && (
								<View className="infoRow">
									<Chip
										label={`발신자: ${logSetting.sender}`}
										variant="outlined"
										size="small"
									/>
									<Chip
										label={`스케줄: ${logSetting.schedule}`}
										variant="outlined"
										size="small"
									/>
								</View>
							)}

							{/* Add new recipient */}
							<View className="addRow">
								<TextField
									label="이름"
									value={newName}
									onChange={e => setNewName(e.target.value)}
									size="small"
								/>
								<TextField
									label="이메일"
									value={newEmail}
									onChange={e => setNewEmail(e.target.value)}
									size="small"
									type="email"
									style={{ flex: 1 }}
								/>
								<Button
									variant="outlined"
									startIcon={<AddIcon />}
									onClick={handleAdd}
									disabled={!newEmail || !newName}
								>
									추가
								</Button>
							</View>

							{/* Recipients Table */}
							<TableContainer component={Paper} variant="outlined">
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>#</TableCell>
											<TableCell>이름</TableCell>
											<TableCell>이메일</TableCell>
											<TableCell align="center">활성</TableCell>
											<TableCell align="center">삭제</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{(logSetting?.recipients ?? []).map(
											(recipient: LogRecipientType, idx: number) => (
												<TableRow key={`${recipient.email}-${idx}`}>
													<TableCell>{idx + 1}</TableCell>
													<TableCell>{recipient.name}</TableCell>
													<TableCell>{recipient.email}</TableCell>
													<TableCell align="center">
														<Switch
															checked={recipient.active}
															onChange={() => handleToggleActive(idx)}
															color="primary"
														/>
													</TableCell>
													<TableCell align="center">
														<IconButton
															size="small"
															color="error"
															onClick={() => handleDelete(idx)}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</TableCell>
												</TableRow>
											),
										)}
										{(logSetting?.recipients ?? []).length === 0 && (
											<TableRow>
												<TableCell colSpan={5} align="center" style={{ padding: '2rem' }}>
													수신자가 없습니다
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</View>
			</BoxView>
		</View>
	)
}

export default LogSettingsPage

const style = css({
	'.settingsContainer': {
		width: '100%',
		padding: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
	},

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '10rem',
	},

	'.infoRow': {
		display: 'flex',
		gap: '0.5rem',
	},

	'.addRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.75rem',
	},
})
